import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

import SearchProviderForm from "./SearchProviderForm";
import { Field, Provider, ProviderData } from "./SearchProvider.types";
import { NearbyRegion, SearchProviderContext } from "./SearchProviderContext";
import { Option } from "react-google-places-autocomplete/build/types";
import MemoArrowIcon from "../Components/ArrowIcon";
import MemoDownloadIcon from "../Components/DownloadIcon";
import SearchProviderMap from "./SearchProviderMap";
import ProviderListItem from "./ProviderListResult";
import { geocodeByPlaceId } from "react-google-places-autocomplete";

const SearchProviderTool: React.FC<ProviderData> = ({
  configuration,
  providers,
}) => {
  const [showMap, setShowMap] = useState<boolean>(
    configuration.defaultDisplay === "map"
  );
  const [supportsMap, setSupportsMap] = useState<boolean>(
    configuration.showMap || true
  );
  const [selectedListItem, setSelectedListItem] = useState<Provider | null>();
  const [showResults, setShowResults] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [nearbyRegions, setNearbyRegions] = useState<NearbyRegion[]>([]);
  const [selectedNearbyRegion, setSelectedNearbyRegion] =
    useState<Option | null>();

  const [filteredProviders, setFilteredProviders] =
    useState<Provider[]>(providers);

  useEffect(() => {
    if (!configuration || !configuration.fields) return;

    // results are hidden by default if you have a map
    setShowResults(
      !configuration.fields.find(
        (field) => field.type === "address" && field.showInSearch
      )
    );
  }, [configuration]);

  const resultsText = (): string | null => {
    if (!filteredProviders.length) return "0 results";

    return (
      `${filteredProviders.length} ` +
      `${filteredProviders.length === 1 ? `result` : `results`}`
    );
  };

  useEffect(() => {
    // shuffle the providers
    setFilteredProviders(shuffle(providers));
  }, [providers]);

  const setProviders = (updated) => {
    setFilteredProviders(shuffle(updated));
  };

  // on mobile map is not supported
  useEffect(() => {
    // add resize event listener
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSupportsMap(false);
      } else {
        setSupportsMap(configuration.showMap || true);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const totalMatchingProviders = filteredProviders.length;

  let mapWrapperStyle = {
    width: showMap ? "50%" : 0,
  };

  return (
    <>
      <SearchProviderContext.Provider
        value={{
          totalMatchingProviders,
          providers,
          setProviders,
          setLoading,
          showResults,
          setShowResults,
          supportsMap,
          manualPlace: selectedNearbyRegion ?? null,
          setNearbyRegions,
        }}
      >
        <SearchProviderForm configuration={configuration} />
      </SearchProviderContext.Provider>

      <div
        className={`provider__data ${loading ? "provider__data--loading" : ""}`}
      >
        {loading && <div className="provider__loading"></div>}

        {showResults && !loading && (
          <>
            {filteredProviders.length > 0 && (
              <div className="provider__controls no-print">
                <p className="provider__controls__count">
                  Showing: <strong>{resultsText()}</strong>
                </p>

                {configuration.showMap && supportsMap ? (
                  <button
                    className="provider__controls__map-btn"
                    onClick={() => {
                      setShowMap((prev) => !prev);
                    }}
                  >
                    {showMap ? "Hide Map" : "Show Map"}
                  </button>
                ) : null}
              </div>
            )}

            {filteredProviders.length > 0 ? (
              <div
                className={"provider__results" + (showMap ? " map-open" : "")}
              >
                <motion.ul
                  initial={{ width: "100%" }}
                  animate={{ width: showMap ? "50%" : "100%" }}
                  className="provider__results__list"
                >
                  {filteredProviders.map((provider: Provider) => {
                    return (
                      <ProviderListItem
                        key={provider.id}
                        provider={provider}
                        selected={selectedListItem?.id === provider.id}
                        configuration={configuration}
                        onClick={(p) => {
                          if (showMap) {
                            setSelectedListItem(p);
                          }
                        }}
                      />
                    );
                  })}
                </motion.ul>
                {supportsMap && (
                  <motion.div
                    className="provider__map"
                    initial={{ width: 0 }}
                    animate={mapWrapperStyle}
                  >
                    <SearchProviderMap
                      providers={filteredProviders}
                      selectedProviderId={selectedListItem?.id}
                      onMarkerClick={(provider) => {
                        setSelectedListItem(provider);
                      }}
                    />
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="provider__results provider__results--none">
                <h2 className="h4">
                  {resultsText()} found with your criteria.
                </h2>

                <p>Try searching a different region or criteria.</p>

                {nearbyRegions && nearbyRegions.length > 0 && (
                  <div className="provider__nearby">
                    <p>
                      <strong>Search in a nearby region</strong>
                    </p>

                    <ul>
                      {nearbyRegions.map((region, idx) => (
                        <li key={idx} className="provider__nearby-item">
                          <MemoArrowIcon />

                          <a
                            onClick={async () => {
                              setLoading(true);

                              const place = await geocodeByPlaceId(
                                region.place_id
                              );

                              // update the place_id in the URL
                              const searchParams = new URLSearchParams(
                                window.location.search
                              );

                              searchParams.set("place_id", region.place_id);

                              window.history.pushState(
                                {},
                                "",
                                `${window.location.pathname}?${searchParams}`
                              );

                              if (place && place.length > 0) {
                                setSelectedNearbyRegion({
                                  label: region.name,
                                  value: place[0],
                                });
                              }

                              setLoading(false);
                            }}
                          >
                            {region.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="page-actions no-print">
              <div className="page-actions__inner">
                <button
                  type="button"
                  className="provider__download"
                  onClick={() => window.print()}
                >
                  Print this list of providers
                  <MemoDownloadIcon />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

/**
 * Get the heading field from the provider.
 * @param {Provider} provider - The provider object
 * @return {string} The label of the heading field, or an empty string if not found
 */
export const getHeadingField = (provider: Provider): string => {
  const title = provider.title || "";

  const headingField = provider.fields.find(
    (field) => field.type === "heading"
  );

  if (headingField) {
    return headingField.value?.toString() || title;
  }

  return title;
};

/**
 * Get the text and number fields from the provider's fields.
 * @param {Provider} provider - the provider object
 * @return {Field[]} the text and number fields from the provider
 */
export const getTextFields = (provider: Provider): Field[] => {
  return provider.fields.filter(
    (field) => field.type === "text" || field.type === "number"
  );
};

/**
 * Get the region fields from the provider.
 * @param {Provider} provider - the provider to get region fields from
 * @return {Field[]} an array of region fields
 */
export const getRegionFields = (provider: Provider): Field[] => {
  return provider.fields.filter((field) => field.type === "regions");
};

export const getTagField = (provider: Provider): Field | null => {
  return provider.fields.find((field) => field.type === "commaSeparatedList");
};

/**
 * Returns a Google Maps link based on the provided field coordinates.
 * @param {Field} field - the field containing the coordinates
 * @return {string} the Google Maps link
 */
export const getMapLink = (field: Field): string => {
  if (!field.value) return "";

  if (!field.value["lat"] || !field.value["lng"]) {
    return "";
  }

  const latLng = encodeURIComponent(
    field.value["lat"] + "," + field.value["lng"]
  );

  return `https://www.google.co.nz/maps/search/?api=1&query=${latLng}`;
};

/**
 * Retrieves the link fields from the specified provider.
 * @param {Provider} provider - The provider from which to retrieve the link fields
 * @return {Field[]} The link fields from the specified provider
 */
export const getLinkFields = (provider: Provider): Field[] => {
  return provider.fields.filter(
    (field) =>
      field.type === "email" ||
      field.type === "phone" ||
      field.type === "url" ||
      field.type === "address"
  );
};

const shuffle = (array: Provider[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

export default SearchProviderTool;
